<!--
  ItemCard component, where, if no file set previously, a file can be dragged and uploaded
  If a file is set, it will preview this file
-->
<template>
  <div class="drag-and-drop-item-card">

    <!-- if fileItem is not required, show the preview card version -->
    <v-card
        v-if="!required"
        tile
        elevation="!cardElevation ? 0 : 1"
        :class="wasRequired ? 'required-border-fulfilled pa-2' : 'pa-2'"
        @click="editItem"
        :min-height="height ? height : ''"
    >
      <!-- view, download and delete icon btns -->
      <v-row>
        <v-col v-if="showStatus" :class="'docu-status-badge ' + getStatusClass">
          <span :class="getStatusClass + ' text-center'">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="white"
                    v-on="on"
                    v-bind="attrs"
                >
                  {{ getStatusIcon }}
                </v-icon>
              </template>
              {{ $t('components:drag-and-drop-file.tooltips.' + getStatusClass) }}
            </v-tooltip>
          </span>
        </v-col>
        <v-col class="text-right">
          <v-tooltip bottom :disabled="!viewIconTooltip">
            <template v-slot:activator="{ on, attrs}">
              <v-btn
                  v-if="item"
                  class="mr-2"
                  icon
                  @click.native.stop="viewFile"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon color="primary">visibility</v-icon>
              </v-btn>
            </template>
            <span>{{ viewIconTooltip }}</span>
          </v-tooltip>

          <v-tooltip bottom :disabled="!downloadIconTooltip">
            <template v-slot:activator="{ on, attrs}">
              <v-btn
                  v-if="item"
                  @click.native.stop="downloadFile"
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>cloud_download</v-icon>
              </v-btn>
            </template>
            <span>{{ downloadIconTooltip }}</span>
          </v-tooltip>

          <v-btn
              v-if="item && !hideDelete"
              class="text-capitalize"
              @click.native.stop="deleteItem"
              text
              small
              color="errorDark"
          >
            {{ $t('buttons:delete')}}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Preview image of the document -->
      <v-card-text class="text-center" v-if="isPreviewFile">
        <pdf
            ref="pdf"
            v-if="item.fileType === 'application/pdf'"
            :src="item.src"
            class="pdf docu-preview"
            v-on:click.native="viewFile"
        >
        </pdf>

        <div style="height: 136px;" v-else-if="item.fileType === 'image/jpeg' || item.fileType === 'image/png'">
          <v-img
              :src="item.src"
              contain
              max-height="136"
              @click="viewFile"
          />
        </div>
      </v-card-text>

      <!-- Title of card -->
      <v-card-title class="px-4 pt-0">
        <p>{{ title }}</p>
      </v-card-title>

      <!-- Description of card -->
      <v-card-text class="pb-0">
        {{ description }}
      </v-card-text>

      <!-- Additional Info of card -->
      <v-card-text class="pb-0">
        {{ additionalInfo }}
      </v-card-text>
    </v-card>

    <!-- if the fileItem is required, show the drag and drop field with the document type -->
    <v-card
      v-else
      tile
      elevation="!cardElevation ? 0 : 1"
      :class="getPriorityColor + ' pa-2'"
      :min-height="height ? height : ''"
      @click="$refs.fileInput.$refs.input.click()"
      @drop.prevent="onDrop($event)"
      @dragover.prevent="dragover = true"
      @dragenter.prevent="dragover = true"
      @dragleave.prevent="dragover = false"

    >
      <v-card-text :class="'my-auto drag-and-drop--no-flex ' + (dragover ? 'grey lighten-2' : '')">
        <v-row class="d-flex flex-column" dense align="center" justify="center">
          <v-icon :class="[dragover ? 'mt-2, mb-6' : 'mt-5']" size="60">
            cloud_upload
          </v-icon>
          <p class="text-center mb-0">
            {{ $t('components:drag-and-drop-file.title') }}
          </p>
        </v-row>

        <v-file-input
            v-show="file"
            ref="fileInput"
            accept="image/*, application/pdf"
            :label="$t('components:common.filename')"
            show-size
            v-model="file"
            prepend-icon=""
            truncate-length="25"
            :rules="[
            ruleRequired($t('utils:rules.requiredFile')),
            ruleMaxFileSize(fileSizeLimit,$t('utils:rules.filesize', {size : '20 MB'}))
          ]"
            @change="onSelect"
        />
      </v-card-text>

      <v-card-title>
        <p>{{ title }}</p>
      </v-card-title>
    </v-card>
  </div>
</template>

<script>
import {ruleMaxFileSize, ruleRequired} from "@/modules/core/services/rules";

export default {
  name: 'DragAndDropItemCard',
  components: {
    pdf: () => import('vue-pdf')
  },
  props: {
    item: Object, // item data to display
    title: String, // title for card
    description: String, // description for card
    additionalInfo: String, // additional text
    required: Boolean, // bool to set additional styling for required items
    wasRequired: Boolean, // if item has required attribute
    height: String, // set height of card
    priority: Number, // priority for required items
    downloadIconTooltip: String, // Tooltip for download icon
    viewIconTooltip: String, // Tooltip for view icon
    hideDelete: Boolean, // Hide delete Btn
    showStatus: Boolean, // If the status badge of the document should be shown
  },
  data() {
    return {
      dragover: false,
      file: null
    }
  },
  computed: {
    fileSizeLimit() {
      return process.env.VUE_APP_UPLOAD_SIZE_DOCUMENT;
    },
    isPreviewFile() {
      return this.item.fileType === 'application/pdf'
          || this.item.fileType === 'image/jpeg'
          || this.item.fileType === 'image/png';
    },
    getPriorityColor() {
      if (this.priority) {
        return 'required-border-priority-' + this.priority;
      } else {
        return ''
      }
    },
    getStatusClass() {
      if (this.showStatus) {
        // if document has been generated
        if (this.item.generated) {
          // if document has to be signed
          if (this.item.toBeSigned) {
            // if document has been signed
            if (this.item.signed) {
              return 'generate-signage-req-signed'
            } else {
              return 'generate-signage-req-not-signed'
            }
          } else {
            return 'generate-no-signage-req'
          }
        } else {
          if (this.item.signed) {
            return 'generate-signage-req-signed'
          } else {
            return '';
          }
        }
      } else {
        return '';
      }
    },

    getStatusIcon() {
      if (this.getStatusClass === 'generate-signage-req-signed') {
        return 'done_all'
      } else if (this.getStatusClass === 'generate-signage-req-not-signed') {
        return 'warning'
      } else if (this.getStatusClass === 'generate-no-signage-req') {
        return 'loop'
      } else {
        return ''
      }
    }
  },
  methods: {
    ruleRequired,
    ruleMaxFileSize,

    onSelect(e) {
      this.$emit('fileChange', e)
    },

    onDrop(e) {
      this.dragover = false;
      this.file = e.dataTransfer.files[0]
      this.$emit('fileChange', this.file)
    },

    editItem() {
      this.$emit("editItem", this.item);
    },

    viewFile() {
      this.$emit("viewFile", this.item);
    },

    downloadFile() {
      this.$emit("downloadFile", this.item);
    },

    deleteItem() {
      this.$emit("deleteItem", this.item);
    }
  }
}
</script>
<style lang="scss">
.drag-and-drop-item-card {
  .required-border-priority-1 {
    border-bottom: 5px solid var(--v-scaleRed-base) !important;
  }

  .required-border-priority-2 {
    border-bottom: 5px solid var(--v-scaleOrange-base) !important;
  }

  .required-border-priority-3 {
    border-bottom: 5px solid var(--v-scaleYellow-base) !important;
  }

  .required-border-fulfilled {
    border-bottom: 5px solid var(--v-scaleGreen-base) !important;
  }

  .pdf {
    width: 100px!important;
    margin-right: auto;
    margin-left: auto;
  }

  .drag-and-drop--no-flex {
    height: 100%!important;
    border: 2px dashed grey!important;
  }

  .docu-status-badge {
    top: 0;
    position: absolute;
    z-index: 1;
    border-bottom-right-radius: 50%;
    border-bottom-left-radius: 50%;
    box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
    padding: 10px;
    left: 0;
    width: 20%;

    border-left: 2px solid white;
    border-right: 2px solid white;
    border-bottom: 2px solid white;
  }

  .docu-status-badge.generate-signage-req-signed {
    background-color: #13be9c;
    outline: 3px solid #13be9c;

    .v-icon {
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .docu-status-badge.generate-signage-req-not-signed {
    background-color: #df8b5d;
    outline: 3px solid #df8b5d;

    .v-icon {
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .docu-status-badge.generate-no-signage-req {
    background-color: #5e64df;
    outline: 3px solid #5e64df;

    .v-icon {
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
  }
}
</style>